<template>
  <div class="relative bg-cw-green-light">
    <SuccessScreen stroke-color="#199163">
      <div class="flex flex-col heightMd:mt-0 w-[290px] heightMd:w-[200px] heightSm:gap-2 heightMd:gap-4 heightMd:mb-9 mb-16">
        <p class="heightMd:text-lg text-2xl text-center">You have traded your card with {{ activeOffer.user?.username }}</p>
      </div>
      <div class="swap-root flex justify-between gap-10 heightSm:gap-4 heightMd:gap-6 items-center" :class="{ 'swap-stage': isAnimating }">
        <div ref="leftEl" class="left-card px-2.5">
          <GameCardItem :card="cardGiven" />
        </div>
        <div class="arrows flex items-center">
          <ArrowFatLeft class="text-cw-green-dark lg:w-8 lg:h-8 xl:h-10 xl:w-10 heightMd:w-5 heightMd:h-5 mr-5 xl:-mb-4 lg:-mb-2 heightMd:mr-3 heightMd:-mb-2" />
          <ArrowFatRight class="text-cw-green-dark heightMd:w-5 heightMd:h-5 lg:w-8 lg:h-8 xl:h-10 xl:w-10 ml-5 heightMd:ml-3" />
        </div>

        <div ref="rightEl" class="right-card px-2.5">
          <GameCardItem :card="cardReceived" />
        </div>
      </div>
    </SuccessScreen>
  </div>
</template>

<script setup lang="ts">
import GameCardItem from '@/components/game-play/GameCardItem.vue'
import SuccessScreen from '@/components/game-play/SuccessScreen.vue'
import ArrowFatRight from '@/assets/svgs/icons/arrow-fat-right.svg'
import ArrowFatLeft from '@/assets/svgs/icons/arrow-fat-left.svg'
import { useGameArea } from '@/composables/useGameArea'
import { useGameStore } from '@/stores/game'
import type { CardT } from '@/types/Game'
import { computed, onMounted, ref, nextTick, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { GameEvent } from '@/types/Events'
import useSocket from '@/composables/useSocket'
import { useHelpers } from '@/composables/useHelpers'
import { useSound } from '@/composables/useSound'
const { getCardById, resumeGameState, getPlayerById } = useGameStore()
const { handleCustomError } = useHelpers()
const { playCardTraded } = useSound()

const { tradeOfferPlayerId, setActiveGameAreaScreen } = useGameArea()
const { gameState } = storeToRefs(useGameStore())
const socket = useSocket()

const isAnimating = ref(false)
const leftEl = ref<HTMLElement | null>(null)
const rightEl = ref<HTMLElement | null>(null)
const hasStarted = ref(false)
const isCompleting = ref(false)
let raf1 = 0
let raf2 = 0
let timer = 0

const activeOffer = computed(() => {
  const offer = gameState.value!.activeTrade!.offers.find(offer => offer.playerId == tradeOfferPlayerId.value)
  return {
    ...offer,
    user: getPlayerById(offer!.playerId)
  }
})

const cardReceived = computed(() => {
  return getCardById(activeOffer.value!.cardId as string) as CardT
})

const cardGiven = computed(() => {
  return getCardById(gameState.value!.activeTrade!.swapCardId as string) as CardT
})

onMounted(async () => {
  if (hasStarted.value) return
  hasStarted.value = true

  playCardTraded()

  // Ensure DOM is painted before starting animation
  await nextTick()
  const l = leftEl.value
  const r = rightEl.value
  if (l && r) {
    const lb = l.getBoundingClientRect()
    const rb = r.getBoundingClientRect()
    const dxLeftToRight = rb.left - lb.left
    const dxRightToLeft = lb.left - rb.left
    l.style.setProperty('--dx', `${dxLeftToRight}px`)
    r.style.setProperty('--dx', `${dxRightToLeft}px`)
  }

  raf1 = requestAnimationFrame(async () => {
    // Flush once more then start
    raf2 = requestAnimationFrame(async () => {
      isAnimating.value = true
      const DURATION_MS = 1400 // align with CSS ~700ms + buffer
      timer = window.setTimeout(async () => {
        if (isCompleting.value) return // Prevent multiple executions
        isCompleting.value = true

        try {
          // Complete the trade first, then update UI state
          await socket.sendEventAsync<CardT[]>(GameEvent.CompleteTrade)
          // Only update UI after successful socket operation
          setActiveGameAreaScreen('IDLE_SCREEN')
          resumeGameState()
        } catch (error) {
          handleCustomError(error)
          // On error, still try to recover by resuming state
          resumeGameState()
        }
      }, DURATION_MS)
    })
  })
})

onUnmounted(() => {
  if (raf1) cancelAnimationFrame(raf1)
  if (raf2) cancelAnimationFrame(raf2)
  if (timer) clearTimeout(timer)
})
</script>

<style scoped>
.custom-shadow {
  text-shadow: 4px 0px 0px #000;
}

.swap-root {
  position: relative;
}

.left-card,
.right-card {
  width: max-content;
  transition:
    transform 700ms cubic-bezier(0.22, 0.61, 0.36, 1),
    filter 700ms ease;
  will-change: transform, filter;
}

/* Move the cards toward each other with a gentle tilt */
.swap-stage .left-card {
  transform: translateX(var(--dx, 120%)) rotate(-7deg) scale(1.04);
  filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.2));
  z-index: 2; /* pass above the right card during cross */
}
.swap-stage .right-card {
  transform: translateX(var(--dx, -120%)) rotate(7deg) scale(1.04);
  filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.2));
}

/* Arrow attention pulse while swapping */
.arrows :deep(svg) {
  transition: filter 300ms ease;
}
.swap-stage .arrows :deep(svg) {
  animation: arrowPulse 700ms ease-in-out 1 alternate;
  filter: drop-shadow(0 0 6px rgba(25, 145, 99, 0.45));
}

@keyframes arrowPulse {
  0% {
    transform: translateX(0) scale(1);
    opacity: 0.85;
  }
  100% {
    transform: translateX(6px) scale(1.1);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .left-card,
  .right-card {
    transition: none !important;
    transform: none !important;
    filter: none !important;
  }
  .swap-stage .arrows :deep(svg) {
    animation: none !important;
    filter: none !important;
  }
}
</style>
